// src/api.js
// Base URL comes from the build environment (REACT_APP_API_BASE).
// Unset = same origin (e.g. behind a Kubernetes ingress that routes /api to the backend).
const API_BASE = process.env.REACT_APP_API_BASE || "";

// Get JWT token from localStorage
const getToken = () => localStorage.getItem('access_token');

// Make authenticated API calls
async function authenticatedFetch(url, options = {}) {
  const token = getToken();
  if (!token) {
    throw new Error('No authentication token. Please login first.');
  }
 
  const response = await fetch(url, {
    ...options,
    headers: {
      ...options.headers,
      'Authorization': `Bearer ${token}`,
    },
  });
 
  if (response.status === 401) {
    // Token expired or invalid - remove it and redirect to login
    localStorage.removeItem('access_token');
    window.location.href = '/login';
    throw new Error('Session expired. Please login again.');
  }
 
  return response;
}

export async function createUser(userData) {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/users`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(userData)
  });
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || 'Failed to create user');
  }
  
  return response.json();
}

export async function getAllUsers() {
  const response = await authenticatedFetch(`${API_BASE}/api/admin/users`);
  
  if (!response.ok) {
    throw new Error('Failed to fetch users');
  }
  
  return response.json();
}

export async function toggleUserStatus(username, disabled) {
  const response = await authenticatedFetch(
    `${API_BASE}/api/admin/users/${username}/toggle?disabled=${disabled}`,
    { method: 'PATCH' }
  );
  
  if (!response.ok) {
    throw new Error('Failed to update user status');
  }
  
  return response.json();
}

// Get current user info
export async function getCurrentUser() {
  const token = getToken();
  if (!token) throw new Error('No token');
 
  const response = await authenticatedFetch(`${API_BASE}/api/auth/me`);
  return response.json();
}

export async function checkIsAdmin() {
  try {
    const user = await getCurrentUser();
    return user.is_admin || false;
  } catch {
    return false;
  }
}

// Process PDFs with real upload progress (XHR). onProgress receives 0..1
export function processPdfsWithProgress(files, onProgress) {
  return new Promise((resolve, reject) => {
    const form = new FormData();
    for (const f of files) form.append("files", f);

    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${API_BASE}/api/process`);
    xhr.setRequestHeader("Authorization", `Bearer ${getToken()}`);
    xhr.responseType = "json";

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) onProgress(e.loaded / e.total);
    };
    xhr.upload.onload = () => {
      // transfer complete (server still processing) - signal 100% of upload phase
      if (onProgress) onProgress(1);
    };
    xhr.onload = () => {
      if (xhr.status === 401) {
        localStorage.removeItem('access_token');
        window.location.href = '/login';
        reject(new Error('Session expired. Please login again.'));
      } else if (xhr.status >= 200 && xhr.status < 300) {
        resolve(xhr.response);
      } else {
        reject(new Error(`Backend error ${xhr.status}: ${JSON.stringify(xhr.response).slice(0, 300)}`));
      }
    };
    xhr.onerror = () => reject(new Error("Network error while contacting backend"));
    xhr.send(form);
  });
}

// Process PDFs (requires authentication)
export async function processPdfs(files) {
  const form = new FormData();
  for (const f of files) {
    form.append("files", f);
  }
 
  try {
    const response = await authenticatedFetch(`${API_BASE}/api/process`, {
      method: "POST",
      body: form,
    });
   
    if (!response.ok) {
      const text = await response.text().catch(() => "");
      throw new Error(`Backend error ${response.status}: ${text}`);
    }
   
    return response.json();
  } catch (error) {
    console.error('Process PDFs error:', error);
    throw error;
  }
}

// Authentication functions
export async function login(username, password) {
  const formData = new FormData();
  formData.append('username', username);
  formData.append('password', password);
 
  const response = await fetch(`${API_BASE}/api/auth/login`, {
    method: 'POST',
    body: formData,
  });
 
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || 'Login failed');
  }
 
  const data = await response.json();
  localStorage.setItem('access_token', data.access_token);
  return data;
}

// Check if user is logged in
export function isAuthenticated() {
  const token = getToken();
  if (!token) return false;
 
  try {
    // Basic JWT expiration check
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp * 1000 > Date.now();
  } catch (e) {
    return false;
  }
}

// Logout
export function logout() {
  localStorage.removeItem('access_token');
  window.location.href = '/';
}

